import React from 'react';

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ fontSize: 20, margin: '28px 0 10px' }}>{children}</h2>
);

const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ fontSize: 15, margin: '18px 0 8px' }}>{children}</h3>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ lineHeight: 1.7, marginBottom: 12 }}>{children}</p>
);

const LI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li style={{ lineHeight: 1.7, marginBottom: 6 }}>{children}</li>
);

const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul style={{ paddingLeft: 20, marginBottom: 12 }}>{children}</ul>
);

const TermsOfService: React.FC = () => (
  <div className="section">
    <div className="wrap">
      <div className="measure" style={{ margin: '0 auto' }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
          Trust
        </span>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Terms of Service</h1>
        <p className="meta" style={{ marginBottom: 24 }}>Effective date: August 2025</p>

        <P>
          Welcome to Common Room. These Terms of Service ("Terms") govern your use of our
          mentorship platform and services. By using Common Room, you agree to these Terms.
        </P>

        <H2>1. Acceptance of terms</H2>
        <P>
          By accessing or using Common Room, you agree to be bound by these Terms and our
          Privacy Policy. If you don't agree to these Terms, please don't use our services.
        </P>
        <P>
          If you are under 18, you must have your parent or guardian's permission to use our
          services and they must agree to these Terms on your behalf.
        </P>

        <H2>2. Description of service</H2>
        <P>Common Room provides:</P>
        <UL>
          <LI>A marketplace connecting families with verified mentors</LI>
          <LI>Credential verification against official public sources</LI>
          <LI>Session booking, payment, and video conferencing</LI>
          <LI>Session recordings, notes, and written summaries</LI>
          <LI>Messaging between families and mentors</LI>
        </UL>

        <H2>3. User accounts</H2>
        <H3>Account creation</H3>
        <P>
          To use Common Room, you must create an account with accurate and complete information.
          You are responsible for maintaining the confidentiality of your account credentials.
        </P>
        <H3>Account responsibilities</H3>
        <UL>
          <LI>You must provide accurate, current, and complete information</LI>
          <LI>You are responsible for all activities under your account</LI>
          <LI>You must notify us immediately of any unauthorized use</LI>
          <LI>You may not share your account with others</LI>
        </UL>

        <H2>4. Acceptable use</H2>
        <P>
          You agree to use Common Room only for lawful purposes and in accordance with these
          Terms. You agree NOT to:
        </P>
        <UL>
          <LI>Violate any laws or regulations</LI>
          <LI>Submit false or misleading information — including mentor credentials</LI>
          <LI>Interfere with or disrupt our services</LI>
          <LI>Attempt to gain unauthorized access to any part of our service</LI>
          <LI>Use our service to harm, threaten, or harass others</LI>
          <LI>Upload malicious code or viruses</LI>
          <LI>Scrape or copy content without permission</LI>
          <LI>Impersonate another person or entity</LI>
        </UL>

        <H2>5. Intellectual property</H2>
        <H3>Our content</H3>
        <P>
          All content on Common Room, including text, graphics, logos, and software, is owned by
          or licensed to us and is protected by intellectual property laws. You may not copy,
          modify, or distribute our content without permission.
        </P>
        <H3>Your content</H3>
        <P>
          You retain ownership of the content you create using our service. By using Common
          Room, you grant us a license to use, store, and process your content solely to provide
          our services to you.
        </P>

        <H2>6. Third-party services</H2>
        <P>
          Sign-in is provided through Google and payments through Stripe; each is subject to its
          own terms of service. Our service may contain links to third-party websites. We are
          not responsible for the content or practices of these external services.
        </P>

        <H2>7. Payment terms</H2>
        <P>If you purchase sessions or packs:</P>
        <UL>
          <LI>Payment is due at booking</LI>
          <LI>All fees are non-refundable unless otherwise stated</LI>
          <LI>We may change our pricing with 30 days' notice</LI>
          <LI>You are responsible for all applicable taxes</LI>
        </UL>

        <H2>8. Disclaimers</H2>
        <H3>Educational purpose</H3>
        <P>
          Common Room provides access to mentors who share their verified experience. Mentors
          never promise outcomes, and we do not guarantee admission to any college or
          university. Success depends on many factors beyond our control.
        </P>
        <H3>No professional advice</H3>
        <P>
          Our service provides general information and should not be considered professional
          educational consulting. For specific advice, consult with qualified educational
          professionals.
        </P>
        <H3>"As is" service</H3>
        <P>
          We provide our service "as is" without warranties of any kind, either express or
          implied, including but not limited to warranties of merchantability, fitness for a
          particular purpose, or non-infringement.
        </P>

        <H2>9. Limitation of liability</H2>
        <P>
          To the maximum extent permitted by law, Common Room and its affiliates shall not be
          liable for:
        </P>
        <UL>
          <LI>Any indirect, incidental, special, or consequential damages</LI>
          <LI>Loss of profits, data, or use</LI>
          <LI>College admission outcomes or scholarship decisions</LI>
          <LI>Any damages exceeding the amount you paid us in the past 12 months</LI>
        </UL>

        <H2>10. Indemnification</H2>
        <P>
          You agree to indemnify and hold harmless Common Room, its affiliates, and their
          respective officers, directors, employees, and agents from any claims, damages,
          losses, liabilities, and expenses arising from your use of our service or violation of
          these Terms.
        </P>

        <H2>11. Termination</H2>
        <P>
          We may suspend or terminate your account if you violate these Terms or for any other
          reason at our discretion. You may terminate your account at any time through your
          account settings.
        </P>
        <P>
          Upon termination, your right to use our service will immediately cease. We may retain
          certain information as required by law or for legitimate business purposes.
        </P>

        <H2>12. Governing law</H2>
        <P>
          These Terms are governed by the laws of the State of Delaware, United States, without
          regard to its conflict of law provisions. Any disputes shall be resolved in the courts
          of Delaware.
        </P>

        <H2>13. Changes to terms</H2>
        <P>
          We may update these Terms from time to time. We will notify you of material changes
          via email or through our service. Your continued use after changes constitutes
          acceptance of the new Terms.
        </P>

        <H2>14. Contact information</H2>
        <P>
          If you have questions about these Terms of Service, please contact us at
          legal@commonroom.example.
        </P>

        <H2>15. Entire agreement</H2>
        <P>
          These Terms, together with our Privacy Policy, constitute the entire agreement between
          you and Common Room regarding the use of our service.
        </P>
      </div>
    </div>
  </div>
);

export default TermsOfService;
